﻿/*
 * Purpose: Presentational inputs and controls for Create Password flow.
 * Boundary: Pure UI; no local state or effects; fully controlled by parent.
 * Invariants:
 * - Controlled components: value, handlers, and refs come from parent.
 * - Accessible labels and alt text included; suffix actions use buttons.
 * - ASCII only; no Unicode characters in comments or strings.
 */

// Component PasswordInput:
// Controlled field without Show/Hide and Copy actions (moved to separate buttons)
export function PasswordInput({ value, onChange, id, showPassword, className = "" }) {
    // Rendering - labeled input without suffix buttons
    return (
        <label className={`grid gap-1 w-full ${className}`} htmlFor={id}>
            <span className="text-sm text-neutral-300">Password</span>
            <input
                id={id}
                type={showPassword ? "text" : "password"}
                className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none w-full"
                value={value}
                onChange={onChange}
                placeholder="Enter or generate password"
                required
            />
        </label>
    );
}

// Component ConfirmInput:
// Controlled confirm field without Show/Hide button
export function ConfirmInput({ value, onChange, id, showPassword, className = "" }) {
    // Rendering - labeled confirm input without toggle visibility
    return (
        <label className={`grid gap-1 w-full ${className}`} htmlFor={id}>
            <span className="text-sm text-neutral-300">Confirm Password</span>
            <input
                id={id}
                type={showPassword ? "text" : "password"}
                className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none w-full"
                value={value}
                onChange={onChange}
                placeholder="Confirm password"
                required
            />
        </label>
    );
}

// Component TypeSelector:
// Radio group for selecting password generation mode
export function TypeSelector({ value, onChange }) {
    // Rendering - mode selection between random string and phrase
    return (
        <div className="flex flex-col gap-2 md:flex-row md:gap-4">
            <label className="flex items-center gap-2">
                <input type="radio" value="chars" checked={value === "chars"} onChange={onChange} />
                <span className="text-sm text-neutral-300">Random String</span>
            </label>
            <label className="flex items-center gap-2">
                <input type="radio" value="phrase" checked={value === "phrase"} onChange={onChange} />
                <span className="text-sm text-neutral-300">Phrase</span>
            </label>
        </div>
    );
}

// Component LengthInput:
// Controlled numeric input for length or word count based on type
export function LengthInput({ type, value, onChange }) {
    const label = type === "chars" ? "Length (default 20)" : "Words (default 4)";
    const placeholder = type === "chars" ? "20" : "4";
    // Rendering - labeled numeric field with min rule per type
    return (
        <label className="grid gap-1">
            <span className="text-sm text-neutral-300">{label}</span>
            <input
                type="number"
                min={type === "chars" ? 15 : 4}
                className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none w-full md:w-20"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
        </label>
    );
}

// Component GenerateButton:
// Primary CTA to trigger password generation
export function GenerateButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-1 md:px-5 md:py-1 rounded-lg bg-neutral-900 text-white text-sm md:text-base ring-1 ring-neutral-800 focus:ring-emerald-600 disabled:opacity-60"
    >
      Generate
    </button>
  );
}

// Component HideCopyButton:
// Combined Hide | Copy button that looks like one button but has two actions
export function HideCopyButton({ onToggleShow, onCopy, showPassword }) {
  return (
    <div className="flex rounded-lg bg-neutral-900 ring-1 ring-neutral-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggleShow}
        className="px-4 py-1 md:px-6 md:py-1 text-white text-xs md:text-base hover:bg-neutral-800 transition"
      >
        {showPassword ? "Hide" : "Show"}
      </button>
      <div className="w-px bg-neutral-700" />
      <button
        type="button"
        onClick={onCopy}
        className="px-4 py-1 md:px-6 md:py-1 text-white text-xs md:text-base hover:bg-neutral-800 transition"
      >
        Copy
      </button>
    </div>
  );
}